import { useTranslation } from 'react-i18next';

import { useThemeStore, type ThemePreference } from '@/shared/store/themeStore';

const NEXT_LABEL: Record<ThemePreference, 'theme.system' | 'theme.light' | 'theme.dark'> = {
  system: 'theme.system',
  light: 'theme.light',
  dark: 'theme.dark'
};

function Glyph({ preference }: { preference: ThemePreference }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className: 'size-5'
  };

  if (preference === 'dark') {
    return (
      <svg {...common}>
        <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
      </svg>
    );
  }
  if (preference === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2m0 16v2M2 12h2m16 0h2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="3" y="4.5" width="18" height="13" rx="2.5" />
      <path d="M8 21h8M12 17.5V21" />
    </svg>
  );
}

/**
 * Cycles system -> light -> dark. `aria-label` names the CURRENT state rather
 * than the next one, because a screen reader reads the button before the user
 * decides to press it.
 */
export function ThemeToggle() {
  const { t } = useTranslation();
  const preference = useThemeStore((state) => state.preference);
  const cycle = useThemeStore((state) => state.cycle);

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`${t('theme.toggle')}: ${t(NEXT_LABEL[preference])}`}
      title={t(NEXT_LABEL[preference])}
      className="grid size-10 place-items-center rounded-pill text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
    >
      <Glyph preference={preference} />
    </button>
  );
}
